import { useState } from 'react';
import { Check, ChevronsUpDown, FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { SUGGESTED_DOCUMENT_FOLDERS } from './builder-utils';

interface FolderSelectProps {
  value: string;
  onChange: (folder: string) => void;
}

// Destination-folder picker for DOCUMENT blocks. Offers suggested folder names
// but accepts any free-text folder, since folders are plain string labels in the
// documents module (no folder entity).
export function FolderSelect({ value, onChange }: FolderSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const trimmedQuery = query.trim();
  const showCreate =
    trimmedQuery.length > 0 &&
    !SUGGESTED_DOCUMENT_FOLDERS.some((f) => f.toLowerCase() === trimmedQuery.toLowerCase());

  const commit = (folder: string) => {
    onChange(folder);
    setQuery('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {value || 'Select a destination folder…'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search or type a folder…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandGroup heading="Folders">
              {SUGGESTED_DOCUMENT_FOLDERS.map((folder) => (
                <CommandItem key={folder} value={folder} onSelect={() => commit(folder)}>
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      folder === value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {folder}
                </CommandItem>
              ))}
            </CommandGroup>
            {showCreate && (
              <CommandGroup heading="Custom">
                <CommandItem value={`create-${trimmedQuery}`} onSelect={() => commit(trimmedQuery)}>
                  <FolderPlus className="mr-2 h-4 w-4" />
                  Use “{trimmedQuery}”
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
